import { FC, useState } from 'react'
import { getDateTimeFromUtc, getUserAvatar, reactStringReplace, sanitizeUrl, useStores } from '@/utils'
import { IAnswerBase, MessageType } from '@/types'
import { Avatar } from '@/components/atoms/Avatar'
import { EditAndDeleteButtons } from '@/components/atoms/EditAndDeleteButtons'
import { UpdateReplyForm } from '@/components/molecules/UpdateReplyForm'
import Link from 'next/link'
import ReplyToSvg from '@/public/images/common/reply_to_icon.svg'
import { observer } from 'mobx-react'
import classNames from 'classnames'
import { ReplyModal } from '@/components/organisms/ReplyModal'
import { ReplyButtonGroup } from '@/components/molecules/ReplyButtonGroup'
import { URL_PATTERN } from '@/constants'
import styles from './index.module.scss'

type Props = {
  reply: IAnswerBase
  replies: IAnswerBase[]
  setChildReplies: (replies: IAnswerBase[]) => void
  questionerName: string
  onClickDelete?: (id: string) => void
  haveEditAndDelete?: boolean
  parentReply: IAnswerBase
  setParentReply: (parentReply: IAnswerBase) => void
  isDescendant?: boolean
  isQuestioner?: boolean
}

export const ReplyCard: FC<Props> = observer(
  ({
    reply,
    replies,
    setChildReplies,
    questionerName,
    onClickDelete,
    haveEditAndDelete,
    parentReply,
    setParentReply,
    isDescendant = false,
    isQuestioner = false,
  }) => {
    const { questions, viewer, messages } = useStores()
    const [edit, setEdit] = useState<boolean>(false)
    const [isOpenMoreReplies, setIsOpenMoreReplies] = useState<boolean>(false)
    const [descendantReplies, setDescendantReplies] = useState<IAnswerBase[]>(null)
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

    const toggleModal = () => {
      setParentReply(reply)
      setIsModalOpen(!isModalOpen)
    }

    const deleteDescendantReply = async (id: string) => {
      // eslint-disable-next-line no-alert
      if (window.confirm('Are you sure to delete this reply?')) {
        const isSuccessful = await viewer.viewer.removeMyAnswer(id)
        if (isSuccessful) {
          setDescendantReplies(descendantReplies.filter((r) => r.id !== id))

          messages.add({
            type: MessageType.Info,
            body: 'Reply deleted.',
            isTranslated: true,
            ttl: 5000,
          })
        }
      }
    }

    const setDescendantReply = async () => {
      const descendantReply = await questions.fetchReply({ id: reply.id })
      setDescendantReplies(descendantReply?.childReplies)
      setIsOpenMoreReplies(true)
    }

    const containerClassName = classNames(styles.container, {
      [styles.descendantContainer]: isDescendant,
    })

    const nameClassName = classNames(styles.name, {
      [styles.shortName]: haveEditAndDelete,
    })

    return (
      <>
        <div className={containerClassName}>
          <div className={styles.heading}>
            <div className={styles.respondentGroup}>
              <Link href={`/users/${reply?.respondent.username}`}>
                <a>
                  <div className={styles.avatar}>
                    <Avatar src={getUserAvatar(reply?.respondent)} alt={reply?.respondent.name} />
                  </div>
                </a>
              </Link>
              <div className={nameClassName}>{reply?.respondent.name}</div>
              <div className={styles.replyTo}>
                <ReplyToSvg />
              </div>
              <div className={nameClassName}>{reply?.parentReply?.respondent.name}</div>
            </div>
            {haveEditAndDelete && !edit && (
              <EditAndDeleteButtons
                onClickEdit={() => {
                  setEdit(true)
                }}
                onClickDelete={() => onClickDelete(reply.id)}
              />
            )}
          </div>
          <div className={styles.lineContainer}>
            <div className={styles.line} />
            {edit ? (
              <div className={styles.form}>
                <UpdateReplyForm
                  reply={reply}
                  replies={replies}
                  setReplies={(newReplies: IAnswerBase[]) => setChildReplies(newReplies)}
                  clickCancel={() => {
                    setEdit(false)
                  }}
                  setEdit={setEdit}
                />
              </div>
            ) : (
              <div>
                <div className={styles.timeGroup}>
                  {questionerName === reply.respondent.username && <div className={styles.questioner}>Questioner</div>}
                  <div className={styles.time}>{getDateTimeFromUtc(reply?.updatedAt, 'YYYY/M/D HH:mm')}</div>
                </div>
                <p className={styles.body}>
                  {reactStringReplace(reply?.body, URL_PATTERN, (match, i) => (
                    <a key={i} href={sanitizeUrl(match)} target='_blank' rel='noreferrer'>
                      {match}
                    </a>
                  ))}
                </p>
              </div>
            )}
          </div>
          <ReplyButtonGroup
            toggleModal={toggleModal}
            respondent={reply.respondent}
            answerId={reply.id}
            amountOfThanks={Number(reply.amount)}
          />
          {reply?.repliesCount > 0 && !isOpenMoreReplies && (
            <div
              className={styles.viewMore}
              role='button'
              tabIndex={0}
              onClick={setDescendantReply}
              onKeyPress={setDescendantReply}
            >
              View more replies
            </div>
          )}
          {isOpenMoreReplies && descendantReplies?.length > 0 && (
            <div>
              {descendantReplies.map((r) => {
                return (
                  <ReplyCard
                    key={r.id}
                    reply={r}
                    replies={descendantReplies}
                    questionerName={questionerName}
                    onClickDelete={deleteDescendantReply}
                    haveEditAndDelete={r.respondent.username === viewer.viewer?.username}
                    parentReply={parentReply}
                    setParentReply={(newParentReply: IAnswerBase) => setParentReply(newParentReply)}
                    setChildReplies={(newChildReplies: IAnswerBase[]) => setDescendantReplies(newChildReplies)}
                    isDescendant
                    isQuestioner={isQuestioner}
                  />
                )
              })}
            </div>
          )}
        </div>
        <ReplyModal
          parentReply={parentReply}
          childReplies={replies}
          setReplies={(newReplies: IAnswerBase[]) => setChildReplies(newReplies)}
          isQuestioner={isQuestioner}
          isModalOpen={isModalOpen}
          setIsModalOpen={(isOpen: boolean) => setIsModalOpen(isOpen)}
        />
      </>
    )
  }
)
